import React, {useEffect, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import {api, API_BASE, getCookie} from '../../api';
import Layout from '../components/Layout';

export default function Receipts() {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef();
  const cameraInputRef = useRef();

  useEffect(() => { loadReceipts(); }, []);

  const loadReceipts = () => {
    api('/receipts').then(r => r.json()).then(setReceipts).finally(() => setLoading(false));
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    const jwtToken = getCookie('jwtToken');
    const tenantId = getCookie('tenantId');
    const headers = {};
    if (jwtToken) headers['Authorization'] = `Bearer ${jwtToken}`;
    if (tenantId) headers['X-Tenant-Id'] = tenantId;
    try {
      const res = await fetch(`${API_BASE}/receipts`, { method: 'POST', headers, body: formData });
      if (res.ok) {
        loadReceipts();
      } else {
        setError('Failed to upload receipt. Please try again.');
      }
    } catch {
      setError('Unable to connect. Please check your connection and try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (cameraInputRef.current) cameraInputRef.current.value = '';
    }
  };

  const statusBadge = (status) => {
    const colors = {
      PROCESSING: 'bg-yellow-100 text-yellow-800',
      EXTRACTED: 'bg-blue-100 text-blue-800',
      REVIEWED: 'bg-green-100 text-green-800',
      RECORDED: 'bg-gray-100 text-gray-800',
    };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>{status}</span>;
  };

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Receipts</h2>
        <div className="flex gap-2">
          <input type="file" ref={cameraInputRef} onChange={handleUpload} accept="image/*" capture="environment" className="hidden" />
          <input type="file" ref={fileInputRef} onChange={handleUpload} accept="image/*,.pdf" className="hidden" />
          <button onClick={() => cameraInputRef.current.click()} disabled={uploading}
            className="bg-lime-600 text-white px-4 py-2 rounded-lg hover:bg-lime-700 disabled:opacity-50">
            {uploading ? 'Uploading...' : '📷 Take Photo'}
          </button>
          <button onClick={() => fileInputRef.current.click()} disabled={uploading}
            className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 disabled:opacity-50">
            Upload File
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex justify-between items-center">
          {error}
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 ml-4">&times;</button>
        </div>
      )}

      {loading ? <p className="text-gray-500">Loading...</p> : receipts.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-500 mb-4">No receipts yet. Upload a receipt image to get started.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">File</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {receipts.map(r => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <Link to={`/receipts/${r.id}`} className="text-lime-600 hover:text-lime-800 font-medium">{r.fileName}</Link>
                  </td>
                  <td className="px-6 py-4 text-gray-700">{r.vendor || '—'}</td>
                  <td className="px-6 py-4 text-gray-700">{r.receiptDate || '—'}</td>
                  <td className="px-6 py-4 text-gray-700">{r.total != null ? `$${r.total.toFixed(2)}` : '—'}</td>
                  <td className="px-6 py-4">{statusBadge(r.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Layout>
  );
}
